import React, { useState } from 'react';

import { Alert, Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import KpiComponent from '@/components/Base/KpiComponent';
import dayjs from 'dayjs';
import ImportCountDefault from '@/components/Base/ImportCountDefault';
import { ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConsolidationSummaryStudents } from '@/features/dashboard/api/getConsolidationSummaryStudents';
import LoadingSummary from '@/features/dashboard/components/LoadingSummary';
import { TitlePages } from '@/components/Base/TitlePage';
import Thermometer from '@/components/Base/Thermometer';
import { useTermometroFinanceiroDetalhes } from '@/features/termometro/getTermometroFinanceiroDetalhes';
import KpiComponentGreen from '@/components/Base/KpiComponentGreen';
import DateFormat from '@/utils/DateFormat.ts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export const TermometroGeralAlunosFinanceiro = ({ routeBack }) => {
  const [open, setOpen] = useState(false);

  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const location = useLocation();
  const { id, name } = location.state || {};

  const { data: summary, isError, refetch, isFetching, isFetched } = useTermometroFinanceiroDetalhes(id);
  const {
    data,
    isFetching: isFetchingStudents,
    isFetched: isFetchedStudents,
  } = useConsolidationSummaryStudents({ year: selectedYear, month: selectedMonth });

  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };

  const onApplyFilter = (selectedYear: number) => {
    setOpen(!!selectedYear);
  };

  const maxMonth = summary?.max_min_values?.maximum_value?.month_order
  const minMonth = summary?.max_min_values?.minimal_score?.month_order
  const maxYear = summary?.max_min_values?.maximum_value?.year
  const minYear = summary?.max_min_values?.minimal_score?.year

  return (
    <>
      <ImportCountDefault open={open} handleClose={handleClose} />

      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack flexDirection={'row'} alignItems={'center'}>
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <TitlePages title={'Termômetro financeiro por aluno'} />
        </Stack>

        <Stack flexDirection={'row'} gap={'20px'}>
          <Button
            onClick={() => navigate('/inadimplencia-recomendacoes-alunos', { state: { id, name } })}
            variant="contained"
            sx={{
              fontWeight: '700',
              borderRadius: '24px',
              fontSize: '14px',
              height: '37px',
              color: '#fff',
              textTransform: 'capitalize',
              backgroundColor: '#15AB51',
            }}
          >
            Visualizar recomendação
          </Button>
        </Stack>
      </Stack>

      <Box
        padding={'15px'}
        sx={{
          border: '1px solid rgba(176, 176, 176, 1)',
          background: '#fff',
          borderRadius: '14px',
        }}
      >
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  refetch();
                }}
              >
                Atualizar
              </Button>
            }
          >
            Não foi possível carregar as informações!
          </Alert>
        )}

        {summary && isFetched && (
          <Stack flexDirection={'row'} gap={'8px'}>
            <Stack
              flexDirection="row"
              maxWidth="100%"
              gap="8px"
              style={{
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'thin',
                scrollbarColor: '#888 #f1f1f1',
              }}
            >
              <KpiComponentGreen title={'Score geral'} 
                value={String(summary.medium_score).replace('.', ',')} 
                // value={"9,5"}
              />
              <KpiComponent title={'Situação'} 
                value={summary.is_active ? 'Ativo' : 'Inativo'} 
                style={{ width: '150px' }}

              />
              <KpiComponent title={'% desconto'} 
                value={`${summary.discount}%`} 
                style={{ width: '150px' }}
                // value={"10%"}
              />
              <KpiComponent title={'Score financeiro'} 
                value={`${summary.financial_score.bolsista ? 'Bolsista' : summary.financial_score.value}`} 
                tooltipText={"Valor anual"}
                style={{ width: '150px' }}
                // value={"8"}
              />
            </Stack>
          </Stack>
        )}
        {isFetching && !summary && <LoadingSummary />}
        <Box>
          <Box sx={{ boxShadow: '0px 4px 4px 0px #00000040' }} m={'30px 0'} p={'20px'}>
            <Thermometer score={summary?.medium_score || 0}/>
          </Box>

          <Box sx={{ boxShadow: '0px 4px 4px 0px #00000040' }} m={'30px 0'} p={'20px'}>
            <Stack flexDirection={'row'} justifyContent={'space-between'}>
              <Box fontSize={'16px'} fontWeight={400}>
                Aluno(a): <span style={{ fontWeight: 600 }}>{`${name ?? summary?.student_name} ${summary?.student_info?.serie ? `- ${summary?.student_info?.serie}` : ''}`}</span>
              </Box>

              {summary?.medium_score < 6 && <Box maxWidth={'477px'} fontStyle={'italic'} color={'#E04A4C'}>
                . Em decorrência do score do aluno abaixo da média, é necessário o registro de uma advertência.
              </Box>}
            </Stack>
            <Stack mt={'20px'} flexDirection={'row'} justifyContent={'space-between'}>
              <Stack maxWidth={'470px'} sx={{ width: '100% !important' }}>
                <Stack mt={'40px'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#3F4254'}>
                    Score geral máximo
                  </Typography>

                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#000'}>
                    {summary?.max_min_values
                      ? String(summary?.max_min_values?.maximum_value?.value).replace('.', ',')
                      : '-'}
                  </Typography>
                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#000'}>
                    {summary?.max_min_values
                      ? `${String(maxMonth).padStart(2, '0')}/${maxYear}`
                      : '-'}
                  </Typography>
                </Stack>

                <Stack maxWidth={'470px'} mt={'40px'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#3F4254'}>
                    Score geral mínimo
                  </Typography>

                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#000'}>
                    {summary?.max_min_values
                      ? String(summary?.max_min_values?.minimal_score?.value).replace('.', ',')
                      : '-'}
                  </Typography>

                  <Typography fontWeight={'600'} fontSize={'14px'} color={'#000'}>
                    {summary?.max_min_values
                      ? `${String(minMonth).padStart(2, '0')}/${minYear}`
                      : '-'}
                  </Typography>
                </Stack>

                <Stack maxWidth={'470px'} mt={'40px'} flexDirection={'row'} justifyContent={'space-between'}>
              <Typography fontWeight={'600'} fontSize={'14px'} color={'#3F4254'}>
                Advertências recebidas
              </Typography>

              <Typography fontWeight={'600'} fontSize={'14px'} color={'#000'}>
                {summary?.total_warnings
                  ? summary.total_warnings
                  : '-'}
              </Typography>
            </Stack>
              </Stack>

              <Stack>
                <Stack flexDirection={'row'} justifyContent={'space-between'} gap={'20px'}>
                  <Box width={'300px'} sx={{ borderRadius: '5px', background: '#fef8cb', padding: '15px' }}>
                    <Typography fontWeight={600} mb={'20px'}>
                      Situação financeira acumulada
                      <Tooltip title={'A situação financeira acumulada considera todos os dados financeiros do aluno até o momento.'} arrow>
                        <IconButton size="small" sx={{ p: 0, ml: '2px', position: 'relative', bottom: '1px' }}>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: '#999' }} />
                        </IconButton>                      
                      </Tooltip>
                    </Typography>
                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.accumulated.accumulated_payments}
                    </Typography>
                    <Typography>Quantidade de contas</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.accumulated.accumulated_expire_payments}
                    </Typography>
                    <Typography>A vencer</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.accumulated.accumulated_expired_payments}
                    </Typography>
                    <Typography>Vencidas</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.accumulated.accumulated_paids_in_time}
                    </Typography>
                    <Typography>quitadas no prazo</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.accumulated.accumulated_paids_after_time}
                    </Typography>
                    <Typography>quitadas com atraso</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {/* {} */}
                      {String(summary?.accumulated.medium_expire_days.toFixed(2)).replace('.', ',')}
                    </Typography>
                    <Typography>Média de dias atraso</Typography>
                  </Box>

                  <Box width={'300px'} sx={{ borderRadius: '5px', background: '#d1e3fe', padding: '15px' }}>
                    <Typography fontWeight={600} mb={'20px'}>
                      Situação financeira ano atual
                    </Typography>
                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.current.total_payments}
                    </Typography>
                    <Typography>Quantidade de contas</Typography>
                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.current.total_expire}
                    </Typography>
                    <Typography>A vencer</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.current.total_expired}
                    </Typography>
                    <Typography>Vencidas</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.current.total_in_time}
                    </Typography>
                    <Typography>quitadas no prazo</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {summary?.current.total_after_time}
                    </Typography>
                    <Typography>quitadas com atraso</Typography>

                    <Typography fontWeight={600} mt={'10px'}>
                      {String(summary?.current.medium_expire_days.toFixed(2)).replace('.', ',')}
                    </Typography>
                    <Typography>Médias de dias atraso</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};
